import { deactivateSubscription, saveSubscription } from "@base/api/push";
import {
  type PushPermissionState,
  getCurrentSubscription,
  getPushPermissionState,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "@base/lib/push";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useState } from "react";

export type PushSubscriptionStatus = "unsupported" | "permission-denied" | "inactive" | "active" | "loading";

/**
 * 푸시 알림 구독 상태를 관리하는 훅.
 *
 * - status: 현재 구독 상태
 * - isProcessing: 구독/해제 진행 중 여부
 * - subscribe(): 알림 권한 요청 → 구독 생성 → 서버 저장
 * - unsubscribe(): 서버 비활성화 → 브라우저 구독 해제
 */
export function usePushSubscription() {
  const [status, setStatus] = useState<PushSubscriptionStatus>("loading");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(function checkInitialSubscriptionState() {
    syncStatus();
  }, []);

  useEffect(function recheckOnForeground() {
    if (!isPushSupported()) return;

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        syncStatus();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  function syncStatus() {
    if (!isPushSupported()) {
      setStatus("unsupported");
      return;
    }

    const permission = getPushPermissionState();
    if (permission === "denied") {
      setStatus("permission-denied");
      return;
    }

    getCurrentSubscription().then((subscription) => {
      setStatus(subscription ? "active" : "inactive");
    });
  }

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (isProcessing) return false;
    setIsProcessing(true);

    try {
      const subscription = await subscribeToPush();
      if (!subscription) {
        setStatus(deriveDeniedOrInactive(getPushPermissionState()));
        return false;
      }

      try {
        await saveSubscription(subscription);
      } catch (error) {
        Sentry.captureException(error, { tags: { feature: "push", step: "save_subscription" } });
        await unsubscribeFromPush();
        setStatus("inactive");
        return false;
      }

      setStatus("active");
      return true;
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "push", step: "browser_subscribe" } });
      setStatus("inactive");
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (isProcessing) return false;
    setIsProcessing(true);

    let serverFailed = false;
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) {
        try {
          await deactivateSubscription(endpoint);
        } catch (error) {
          Sentry.captureException(error, { tags: { feature: "push", step: "deactivate_subscription" } });
          serverFailed = true;
        }
      }
      setStatus("inactive");
      return !serverFailed;
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "push", step: "browser_unsubscribe" } });
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing]);

  return { status, isProcessing, subscribe, unsubscribe } as const;
}

function deriveDeniedOrInactive(permission: PushPermissionState): PushSubscriptionStatus {
  return permission === "denied" ? "permission-denied" : "inactive";
}
